<template>
  <div class="header__overlay"></div>
  <div id="header" class="header" :class="{ 'is-hide-header-nav': !shouldHideNavigation }" @keydown.esc="close">
    <div class="header__inner container" :class="{ 'is-hide-navigation': !shouldHideNavigation }">
      <nuxt-link to="/" class="header__logo" aria-label="Public Trust Home" @click="scrollToTop">
        <Logo class="is-header" />
      </nuxt-link>

      <template v-if="shouldHideNavigation">
        <div class="header__nav-toggler">
          <header-nav-toggler />
        </div>
        <div ref="headerNav" class="header__nav">
          <app-header-extras class="header__extras is-main" />
          <app-main-nav v-if="navs.header" :nav="navs.header" class="header__mega-nav" />
          <app-header-extras-sec class="header__extras is-sec" />
        </div>
      </template>
    </div>
    <hr class="header__sep" role="presentation" />
  </div>
  <template v-if="shouldHideNavigation">
    <mobile-nav v-if="navs.header && shouldRenderMobileNav" v-show="navOverlayOpen" :nav="navs.header" />
  </template>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted, computed } from 'vue';
import Logo from '~/components/global/elements/Logo.vue';
import AppMainNav from '~/components/ui/layout/AppMainNav.vue';
import AppHeaderExtras from '~/components/ui/layout/AppHeaderExtras.vue';
import AppHeaderExtrasSec from '~/components/ui/layout/AppHeaderExtrasSec.vue';
import HeaderNavToggler from '~/components/global/components/header-nav/HeaderNavToggler.vue';
import MobileNav from '~/components/ui/layout/mobile-menu/MobileNav.vue';
import { useLayoutNavStore } from '~/stores/layout-nav';

const layoutNavStore = useLayoutNavStore();
const { displayNavigation } = usePages();
const navs = computed(() => layoutNavStore.navs);
const navOverlayOpen = computed(() => layoutNavStore.navOverlayOpen);
const headerNav = ref(null);
const shouldHideNavigation = ref(false);
const shouldRenderMobileNav = ref(false);
let mobileNavIdleHandle: number | null = null;
let mobileNavTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  shouldHideNavigation.value = displayNavigation.value;
  scheduleMobileNavRender();
});

watch(navOverlayOpen, (val) => {
  if (val) {
    shouldRenderMobileNav.value = true;
    cancelScheduledMobileNavRender();
    document.documentElement.classList.add('is-nav-overlay-open');
  } else {
    scrollUnlock();
  }
});

watch(displayNavigation, (val) => {
  shouldHideNavigation.value = val;
  scheduleMobileNavRender();
});

watch(
  () => navs.value.header,
  () => {
    scheduleMobileNavRender();
  }
);

function scheduleMobileNavRender() {
  if (
    !import.meta.client ||
    shouldRenderMobileNav.value ||
    mobileNavIdleHandle !== null ||
    mobileNavTimeoutHandle !== null ||
    !shouldHideNavigation.value ||
    !navs.value.header
  ) {
    return;
  }

  const renderMobileNav = () => {
    shouldRenderMobileNav.value = true;
    mobileNavIdleHandle = null;
    mobileNavTimeoutHandle = null;
  };

  if ('requestIdleCallback' in window) {
    mobileNavIdleHandle = window.requestIdleCallback(renderMobileNav, { timeout: 2000 });
    return;
  }

  mobileNavTimeoutHandle = setTimeout(renderMobileNav, 1200);
}

function cancelScheduledMobileNavRender() {
  if (mobileNavIdleHandle !== null && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(mobileNavIdleHandle);
  }

  if (mobileNavTimeoutHandle !== null) {
    clearTimeout(mobileNavTimeoutHandle);
  }

  mobileNavIdleHandle = null;
  mobileNavTimeoutHandle = null;
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function scrollUnlock() {
  document.documentElement.classList.remove('is-nav-overlay-open');
}

onUnmounted(() => {
  cancelScheduledMobileNavRender();
  scrollUnlock();
});

const close = () => {
  if (layoutNavStore.navOverlayOpen) {
    layoutNavStore.toggleNavOverlay(false);
  }
};
</script>
